import { useNavigate, useParams } from "react-router-dom";
import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import type { CreateLeaderCriteriaRuleModel, UpdateLeaderCriteriaRuleModel } from "~pages/LeaderCriteria/types/leaderCriteriaRuleModel.ts";
import { useCreateLeaderCriteriaRule } from "./useCreateLeaderCriteriaRule.ts";
import { useDeleteLeaderCriteriaRule } from "./useDeleteLeaderCriteriaRule.ts";
import { useUpdateLeaderCriteriaRule } from "./useUpdateLeaderCriteriaRule.ts";

export const useLeaderCriteriaRuleService = () => {
  const { mutateAsync: createLeaderCriteriaRuleFn, isPending: isCreatePending } = useCreateLeaderCriteriaRule();
  const { mutateAsync: updateLeaderCriteriaRuleFn, isPending: isUpdatePending } = useUpdateLeaderCriteriaRule();
  const { mutateAsync: deleteLeaderCriteriaRuleFn, isPending: isDeletePending } = useDeleteLeaderCriteriaRule();
  const { customerId, sport, organizationId, leaderCriteriaId } = useParams();
  const navigate = useNavigate();

  const { create, update, remove } = useApiOperations(ApiOperationItems.LEADER_CRITERIA_RULE);

  const ruleListUrl = `/${customerId}/admin/${sport}/${organizationId}/leaders-qualification/${leaderCriteriaId}/qualifiers`;

  const createLeaderCriteriaRule = async (payload: CreateLeaderCriteriaRuleModel) => {
    const result = await create(() => createLeaderCriteriaRuleFn(payload), {
      successMessageId: "leaders.rule.create.success",
    });

    if (result) {
      navigate(ruleListUrl);
    }

    return result;
  };

  const updateLeaderCriteriaRule = (payload: UpdateLeaderCriteriaRuleModel) =>
    update(() => updateLeaderCriteriaRuleFn(payload), {
      successMessageId: "rule.update.success",
    });

  const deleteLeaderCriteriaRule = async (qualifierId: string) =>
    Boolean(
      await remove(() => deleteLeaderCriteriaRuleFn(qualifierId), {
        successMessageId: "rule.delete.success",
      }),
    );

  return {
    createLeaderCriteriaRule,
    isCreatePending,
    updateLeaderCriteriaRule,
    isUpdatePending,
    deleteLeaderCriteriaRule,
    isDeletePending,
  };
};
